import { supabase } from "./client";
import { getCurrentEmpresaId } from "./auth-utils";

export type ProductionStep = {
  key: string;
  label: string;
  description: string;
  completed: boolean;
  notes: string;
  icon?: string;
  color?: string;
};

export type ProductionTimeline = {
  steps: ProductionStep[];
  current_step_key?: string;
  updated_at: string;
};

export type TimelineConfig = {
  max_steps: number;
  default_steps: Array<{
    key: string;
    order: number;
    label: string;
    description: string;
    color?: string;
    icon?: string;
  }>;
};

// Etapas padrão do sistema (fallback)
const DEFAULT_SYSTEM_STEPS = [
  {
    key: "TECIDO",
    order: 1,
    label: "Tecido",
    description: "Pedido recebido, aguardando compra de tecido",
    color: "blue",
    icon: "Clock",
  },
  {
    key: "CORTE",
    order: 2,
    label: "Corte",
    description: "Trabalho iniciado, corte em andamento",
    color: "blue",
    icon: "Play",
  },
  {
    key: "ESTAMPA",
    order: 3,
    label: "Estampa",
    description: "Etapa de estampa/sublimação em andamento",
    color: "blue",
    icon: "Package",
  },
  {
    key: "COSTURA",
    order: 4,
    label: "Costura",
    description: "Produto aguardando costura/acabamento",
    color: "blue",
    icon: "CheckCircle",
  },
  {
    key: "OFICINA",
    order: 5,
    label: "Oficina",
    description: "Produto enviado para costureira/fornecedor externo",
    color: "blue",
    icon: "Truck",
  },
  {
    key: "PRONTO",
    order: 6,
    label: "Pronto",
    description: "Produto finalizado, pronto para entrega",
    color: "green",
    icon: "CheckCircle2",
  },
  {
    key: "AGUARDANDO_RETIRADA",
    order: 7,
    label: "Aguardando Retirada",
    description: "Produto pronto, aguardando retirada do cliente",
    color: "green",
    icon: "Truck",
  },
  {
    key: "ENTREGUE",
    order: 8,
    label: "Entregue",
    description: "Pedido entregue ao cliente",
    color: "green",
    icon: "CheckCircle2",
  },
];

const DEFAULT_STEPS: ProductionStep[] = DEFAULT_SYSTEM_STEPS.map((step) => ({
  key: step.key,
  label: step.label,
  description: step.description,
  completed: false,
  notes: "",
}));

/**
 * Verificar se uma chave é uma etapa padrão do sistema
 */
export function isDefaultStep(stepKey: string): boolean {
  return DEFAULT_SYSTEM_STEPS.some(step => step.key.toUpperCase() === stepKey.toUpperCase());
}

/**
 * Obter lista das chaves das etapas padrão
 */
export function getDefaultStepKeys(): string[] {
  return DEFAULT_SYSTEM_STEPS.map(step => step.key);
}

/**
 * Obter timeline de produção de um pedido
 */
export async function getProductionTimeline(
  orderId: string
): Promise<ProductionTimeline> {
  try {
    const { data, error } = await supabase
      .from("atelie_orders")
      .select("production_timeline, empresa_id")
      .eq("id", orderId)
      .single();

    if (error) throw error;

    // Carregar configuração da empresa para ter as etapas padrão atualizadas
    const config = await getTimelineConfig();
    const defaultStepsWithConfig = config.default_steps.map((step) => ({
      key: step.key,
      label: step.label,
      description: step.description,
      completed: false,
      notes: "",
      icon: step.icon || "CheckCircle",
      color: step.color || "blue",
    }));

    // Se tem timeline salva, retornar EXATAMENTE como foi salva
    if (data?.production_timeline) {
      const timeline = data.production_timeline as ProductionTimeline;

      if (timeline.steps && timeline.steps.length > 0) {
        console.log(`📊 [TIMELINE CARREGADA] Pedido ${orderId}:`, JSON.stringify(timeline.steps.map(s => ({ key: s.key, label: s.label }))));

        // Gerar current_step_key se não existir (compatibilidade com pedidos antigos)
        let currentStepKey = timeline.current_step_key;
        if (!currentStepKey) {
          const firstIncompleteStep = timeline.steps.find(s => !s.completed);
          currentStepKey = firstIncompleteStep?.key || timeline.steps[0]?.key;
          console.log(`🔑 [GERANDO current_step_key] Pedido ${orderId}: "${currentStepKey}" (compatibilidade com dados antigos)`);
        }

        return {
          steps: timeline.steps,
          current_step_key: currentStepKey,
          updated_at: timeline.updated_at || new Date().toISOString(),
        };
      }
    }

    // Se não existir, retornar etapas padrão configuradas
    return {
      steps: defaultStepsWithConfig,
      current_step_key: "TECIDO",
      updated_at: new Date().toISOString(),
    };
  } catch (error) {
    console.error("Erro ao obter timeline de produção:", error);
    return {
      steps: DEFAULT_STEPS,
      updated_at: new Date().toISOString(),
    };
  }
}

/**
 * Definir a etapa atual de um pedido
 */
export async function setCurrentProductionStep(
  orderId: string,
  stepKey: string
): Promise<{ ok: boolean; error?: string }> {
  try {
    const timeline = await getProductionTimeline(orderId);

    console.log(`🔍 [DEBUG setCurrentProductionStep] Pedido ${orderId}, buscando etapa: "${stepKey}"`);
    console.log(`📋 [DEBUG setCurrentProductionStep] Etapas disponíveis:`, timeline.steps.map(s => ({ key: s.key, label: s.label })));

    // Validar se o step existe (case-insensitive)
    const stepKeyUpperCase = stepKey.toUpperCase();
    const foundStep = timeline.steps.find((s) => s.key.toUpperCase() === stepKeyUpperCase);

    if (!foundStep) {
      console.error(`❌ [ERRO] Etapa "${stepKey}" não encontrada. Keys disponíveis:`, timeline.steps.map(s => s.key));
      return { ok: false, error: `Etapa "${stepKey}" não encontrada` };
    }

    // Usar a chave correta do step encontrado
    const correctStepKey = foundStep.key;

    console.log(`🎯 [DEFININDO ETAPA ATUAL] Pedido ${orderId}: "${correctStepKey}"`);

    return updateProductionTimeline(orderId, timeline.steps, correctStepKey);
  } catch (error: any) {
    console.error("Erro ao definir etapa atual:", error);
    return { ok: false, error: error?.message ?? "Erro ao definir etapa atual" };
  }
}

/**
 * Remover etapas duplicadas (manter apenas a primeira ocorrência de cada key)
 */
export async function removeDuplicateSteps(
  orderId: string
): Promise<{ ok: boolean; error?: string }> {
  try {
    const timeline = await getProductionTimeline(orderId);

    // Manter apenas a primeira ocorrência de cada key
    const seenKeys = new Set<string>();
    const uniqueSteps = timeline.steps.filter((step) => {
      if (seenKeys.has(step.key)) {
        console.log(`🗑️ [REMOVENDO DUPLICATA] ${step.key}: "${step.label}"`);
        return false;
      }
      seenKeys.add(step.key);
      return true;
    });

    if (uniqueSteps.length === timeline.steps.length) {
      return { ok: true };
    }

    console.log(`✅ [DUPLICATAS REMOVIDAS] ${timeline.steps.length} → ${uniqueSteps.length} etapas`);
    return updateProductionTimeline(orderId, uniqueSteps);
  } catch (error: any) {
    console.error("Erro ao remover duplicatas:", error);
    return { ok: false, error: error?.message ?? "Erro ao remover duplicatas" };
  }
}

/**
 * Atualizar um passo específico da timeline
 */
export async function updateProductionStep(
  orderId: string,
  stepKey: string,
  updates: Partial<ProductionStep>
): Promise<{ ok: boolean; error?: string }> {
  try {
    const timeline = await getProductionTimeline(orderId);

    const updatedSteps = timeline.steps.map((step) =>
      step.key === stepKey ? { ...step, ...updates } : step
    );

    return updateProductionTimeline(orderId, updatedSteps, timeline.current_step_key);
  } catch (error: any) {
    console.error("Erro ao atualizar passo da timeline:", error);
    return { ok: false, error: error?.message ?? "Erro ao atualizar timeline" };
  }
}

/**
 * Atualizar múltiplos passos de uma vez
 */
export async function updateProductionTimeline(
  orderId: string,
  steps: ProductionStep[],
  currentStepKey?: string
): Promise<{ ok: boolean; error?: string }> {
  try {
    const config = await getTimelineConfig();

    if (steps.length > config.max_steps) {
      return {
        ok: false,
        error: `Máximo de ${config.max_steps} etapas permitidas`,
      };
    }

    // Se não passou currentStepKey, tentar manter o existente
    let stepKeyToSave = currentStepKey;
    if (!stepKeyToSave) {
      const existing = await getProductionTimeline(orderId);
      stepKeyToSave = existing.current_step_key;
    }

    const timelineUpdate: ProductionTimeline = {
      steps,
      current_step_key: stepKeyToSave,
      updated_at: new Date().toISOString(),
    };

    const { error } = await supabase
      .from("atelie_orders")
      .update({
        production_timeline: timelineUpdate,
      })
      .eq("id", orderId);

    if (error) throw error;

    return { ok: true };
  } catch (error: any) {
    console.error("Erro ao atualizar timeline de produção:", error);
    return { ok: false, error: error?.message ?? "Erro ao atualizar timeline" };
  }
}

/**
 * Adicionar uma nova etapa customizada (até o máximo configurado)
 */
export async function addProductionStep(
  orderId: string,
  newStep: Omit<ProductionStep, "completed">
): Promise<{ ok: boolean; error?: string }> {
  try {
    const timeline = await getProductionTimeline(orderId);
    const config = await getTimelineConfig();

    if (timeline.steps.length >= config.max_steps) {
      return {
        ok: false,
        error: `Máximo de ${config.max_steps} etapas permitidas`,
      };
    }

    // Gerar chave única se for customizada
    const stepKey =
      newStep.key ||
      `CUSTOM_${Math.random().toString(36).substr(2, 9).toUpperCase()}`;

    const updatedSteps = [
      ...timeline.steps,
      {
        ...newStep,
        key: stepKey,
        completed: false,
      },
    ];

    return updateProductionTimeline(orderId, updatedSteps);
  } catch (error: any) {
    console.error("Erro ao adicionar etapa:", error);
    return { ok: false, error: error?.message ?? "Erro ao adicionar etapa" };
  }
}

/**
 * Remover uma etapa customizada
 */
export async function removeProductionStep(
  orderId: string,
  stepKey: string
): Promise<{ ok: boolean; error?: string }> {
  try {
    const timeline = await getProductionTimeline(orderId);

    // Não permitir remover as 6 etapas padrão
    const defaultKeys = DEFAULT_STEPS.map((s) => s.key);
    if (defaultKeys.includes(stepKey)) {
      return {
        ok: false,
        error: "Não é possível remover etapas padrão",
      };
    }

    const updatedSteps = timeline.steps.filter((step) => step.key !== stepKey);

    return updateProductionTimeline(orderId, updatedSteps);
  } catch (error: any) {
    console.error("Erro ao remover etapa:", error);
    return { ok: false, error: error?.message ?? "Erro ao remover etapa" };
  }
}

/**
 * Reordenar etapas da timeline
 */
export async function reorderProductionSteps(
  orderId: string,
  orderedStepKeys: string[]
): Promise<{ ok: boolean; error?: string }> {
  try {
    const timeline = await getProductionTimeline(orderId);

    if (orderedStepKeys.length !== timeline.steps.length) {
      return {
        ok: false,
        error: "Número de etapas não corresponde",
      };
    }

    // Reordenar baseado na ordem fornecida
    const stepMap = new Map(timeline.steps.map((s) => [s.key, s]));
    const reorderedSteps = orderedStepKeys
      .map((key) => stepMap.get(key))
      .filter((step): step is ProductionStep => step !== undefined);

    return updateProductionTimeline(orderId, reorderedSteps);
  } catch (error: any) {
    console.error("Erro ao reordenar etapas:", error);
    return { ok: false, error: error?.message ?? "Erro ao reordenar etapas" };
  }
}

/**
 * Atualizar apenas as observações de uma etapa
 */
export async function updateStepNotes(
  orderId: string,
  stepKey: string,
  notes: string
): Promise<{ ok: boolean; error?: string }> {
  return updateProductionStep(orderId, stepKey, { notes });
}

/**
 * Marcar etapa como concluída
 */
export async function completeProductionStep(
  orderId: string,
  stepKey: string
): Promise<{ ok: boolean; error?: string }> {
  return updateProductionStep(orderId, stepKey, { completed: true });
}

/**
 * Marcar etapa como incompleta
 */
export async function uncompleteProductionStep(
  orderId: string,
  stepKey: string
): Promise<{ ok: boolean; error?: string }> {
  return updateProductionStep(orderId, stepKey, { completed: false });
}

/**
 * Obter configuração de timeline da empresa
 */
export async function getTimelineConfig(): Promise<TimelineConfig> {
  try {
    const empresaId = await getCurrentEmpresaId();
    if (!empresaId) {
      return {
        max_steps: 15,
        default_steps: DEFAULT_SYSTEM_STEPS,
      };
    }

    const { data, error } = await supabase
      .from("empresas")
      .select("production_timeline_config")
      .eq("id", empresaId)
      .single();

    if (error || !data?.production_timeline_config) {
      return {
        max_steps: 15,
        default_steps: DEFAULT_SYSTEM_STEPS,
      };
    }

    return data.production_timeline_config as TimelineConfig;
  } catch (error) {
    console.error("Erro ao obter configuração de timeline:", error);
    return {
      max_steps: 15,
      default_steps: DEFAULT_SYSTEM_STEPS,
    };
  }
}

/**
 * Atualizar configuração de timeline da empresa
 */
export async function updateTimelineConfig(
  config: Partial<TimelineConfig>
): Promise<{ ok: boolean; error?: string }> {
  try {
    const empresaId = await getCurrentEmpresaId();
    if (!empresaId) {
      return { ok: false, error: "Empresa não encontrada" };
    }

    // Validar número máximo de etapas
    if (config.max_steps && config.max_steps > 50) {
      return {
        ok: false,
        error: "Máximo de 50 etapas permitidas",
      };
    }

    if (config.max_steps && config.max_steps < 6) {
      return {
        ok: false,
        error: "Mínimo de 6 etapas requeridas",
      };
    }

    // Validar etapas padrão
    if (
      config.default_steps &&
      config.default_steps.length > (config.max_steps || 15)
    ) {
      return {
        ok: false,
        error: "Número de etapas padrão não pode exceder o máximo permitido",
      };
    }

    // Buscar configuração existente para fazer merge (não sobrescrever tudo!)
    const existingConfig = await getTimelineConfig();
    const mergedConfig = {
      max_steps: config.max_steps ?? existingConfig.max_steps,
      default_steps: config.default_steps ?? existingConfig.default_steps,
    };

    const { error } = await supabase
      .from("empresas")
      .update({
        production_timeline_config: mergedConfig,
      })
      .eq("id", empresaId);

    if (error) throw error;

    return { ok: true };
  } catch (error: any) {
    console.error("Erro ao atualizar configuração de timeline:", error);
    return {
      ok: false,
      error: error?.message ?? "Erro ao atualizar configuração",
    };
  }
}

/**
 * Aplicar ordem das etapas como padrão global
 */
export async function applyStepsAsDefault(
  steps: ProductionStep[]
): Promise<{ ok: boolean; error?: string }> {
  try {
    const empresaId = await getCurrentEmpresaId();
    if (!empresaId) {
      return { ok: false, error: "Empresa não encontrada" };
    }

    // Separar etapas padrão (não CUSTOM_) e reordenar
    const defaultSteps = steps
      .filter(s => !s.key.startsWith("CUSTOM_"))
      .map((step, index) => ({
        key: step.key,
        order: index + 1,
        label: step.label,
        description: step.description,
        color: step.color || "blue",
        icon: step.icon || "CheckCircle",
      }));

    const config: Partial<TimelineConfig> = {
      default_steps: defaultSteps,
    };

    const { error } = await supabase
      .from("empresas")
      .update({
        production_timeline_config: config,
      })
      .eq("id", empresaId);

    if (error) throw error;

    return { ok: true };
  } catch (error: any) {
    console.error("Erro ao aplicar etapas como padrão:", error);
    return {
      ok: false,
      error: error?.message ?? "Erro ao aplicar como padrão",
    };
  }
}
